import { EventType } from "@/analytics/analytics-events";
import {
  SelectColoredAnalysisColorScaleEventProperties,
  SelectColoredAnalysisRangeEventProperties,
  SelectColoredAnalysisToleranceEventProperties,
} from "@/analytics/cloud-analysis-events-properties";
import {
  getDistanceUnitSymbolText,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  ReferencePlaneType,
  setAnalysisColormap,
  setAnalysisTolerance,
  setAnalysisVolumeDisplayIsoLines,
  setAnalysisVolumeDisplayOffset,
  setCadDeviationDisplay,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  areColormapsSame,
  isColormapPresetName,
  PointCloudAnalysisColormapPreset,
  pointCloudAnalysisColormapPresets,
  updateAnalysisSearchDistance,
} from "@/utils/colormap-analysis-utils";
import {
  Dropdown,
  FaroSwitch,
  FaroText,
  NO_TRANSLATE_CLASS,
  TextField,
  grey,
  cyan,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { DistanceUnitTypes } from "@faro-lotv/foundation";
import { Stack } from "@mui/system";
import { Alert, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import PaletteIcon from "@mui/icons-material/Palette";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  initialCadOuterLimit,
  CAD_UNMATCHED_COLOUR,
  isCadDeviationColouring,
  type CadDeviationColouring,
  type CadDeviationSettings,
} from "@/tools/cad-analysis/cad-deviation-colouring";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";

export const colormapOptions = [
  {
    key: PointCloudAnalysisColormapPreset.rainbow,
    label: "Rainbow",
    value: PointCloudAnalysisColormapPreset.rainbow,
  },
  {
    key: PointCloudAnalysisColormapPreset.grayscale,
    label: "Grayscale",
    value: PointCloudAnalysisColormapPreset.grayscale,
  },
  {
    key: PointCloudAnalysisColormapPreset.blueGreenRed,
    label: "Blue-green-red",
    value: PointCloudAnalysisColormapPreset.blueGreenRed,
  },
  {
    key: PointCloudAnalysisColormapPreset.redGreenRed,
    label: "Red-green-red",
    value: PointCloudAnalysisColormapPreset.redGreenRed,
  },
];
const cadToggleStyle = {
  "& .MuiToggleButton-root": { color: grey[0], borderColor: grey[800] },
  "& .MuiToggleButton-root.Mui-selected": {
    color: cyan[400],
    backgroundColor: grey[800],
  },
};

export type ColormapOptionsFormsProps = {
  /** Point cloud object used by analysis */
  pointCloud: PointCloudObject;

  /** Active analysis object to be modified. */
  analysis: PointCloudAnalysis;

  /** Width of the panel (using CSS format) */
  width?: CSSProperties["width"];
};

/** @returns Colormap options UI panel */
export function ColormapOptionsForms({
  pointCloud,
  analysis,
  width,
}: ColormapOptionsFormsProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [colormapName, setColormapName] = useState(() => {
    const initialName = Object.entries(pointCloudAnalysisColormapPresets).find(
      ([, value]) => areColormapsSame(value, analysis.colormap),
    );
    return initialName?.[0];
  });

  const changeColormap = useCallback(
    (newColormap: string) => {
      Analytics.track<SelectColoredAnalysisColorScaleEventProperties>(
        EventType.selectColoredAnalysisColorScale,
        {
          newValue: newColormap,
        },
      );

      setColormapName(newColormap);
      if (isColormapPresetName(newColormap)) {
        const colormap = pointCloudAnalysisColormapPresets[newColormap];
        dispatch(
          setAnalysisColormap({
            analysisId: analysis.id,
            colormap,
          }),
        );
      }
    },
    [analysis.id, dispatch],
  );

  // Keep track of latest analysis values in order to synchronize callbacks
  // to make sure searchDistance always large or equal to tolerance
  const toleranceRef = useRef(analysis.tolerance);
  const searchDistanceRef = useRef(analysis.searchDistance);
  useEffect(() => {
    toleranceRef.current = analysis.tolerance;
    searchDistanceRef.current = analysis.searchDistance;
  }, [analysis.tolerance, analysis.searchDistance]);

  const changeTolerance = useCallback(
    async (tolerance: number) => {
      Analytics.track<SelectColoredAnalysisToleranceEventProperties>(
        EventType.selectColoredAnalysisTolerance,
        { newTolerance: tolerance },
      );

      dispatch(setAnalysisTolerance({ analysisId: analysis.id, tolerance }));
      if (searchDistanceRef.current < tolerance) {
        await updateAnalysisSearchDistance(
          pointCloud,
          analysis,
          tolerance,
          dispatch,
        );
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const changeRange = useCallback(
    async (searchDistance: number) => {
      Analytics.track<SelectColoredAnalysisRangeEventProperties>(
        EventType.selectColoredAnalysisRange,
        { newRange: searchDistance },
      );
      await updateAnalysisSearchDistance(
        pointCloud,
        analysis,
        searchDistance,
        dispatch,
      );
      if (toleranceRef.current > searchDistance) {
        dispatch(
          setAnalysisTolerance({
            analysisId: analysis.id,
            tolerance: searchDistance,
          }),
        );
      }
    },
    [analysis, dispatch, pointCloud],
  );

  const distanceUnit = useDistanceUnit();

  const activeUnit = useMemo(
    () =>
      isMetric(distanceUnit)
        ? DistanceUnitTypes.millimeters
        : DistanceUnitTypes.decimalInches,
    [distanceUnit],
  );

  // Tolerance computation is expensive, to avoid temporary changes of text field while editing,
  // wait 750ms before report last update
  const textInputDebounceTime = 750;

  const volumeAnalysisMode =
    analysis.referencePlaneType === ReferencePlaneType.volume;

  const changeIsoLines = useCallback(
    (isoLinesText: string) => {
      const isoLines = parseInt(isoLinesText, 10);
      if (isNaN(isoLines) || isoLines < 0) {
        return;
      }
      dispatch(
        setAnalysisVolumeDisplayIsoLines({
          analysisId: analysis.id,
          nbIsoLines: isoLines,
        }),
      );
    },
    [analysis.id, dispatch],
  );

  const toggleLiftupPreview = useCallback(() => {
    if (analysis.volumeAnalysisResult === undefined) return;

    // Initial liftup the whole volume mesh 1 meter above the height point
    const LIFT_UP_OFFSET = 1.0;
    const offset =
      analysis.volumeDisplayOffset === undefined
        ? analysis.volumeAnalysisResult.heightMax -
          analysis.volumeAnalysisResult.heightMin +
          LIFT_UP_OFFSET
        : undefined;
    dispatch(
      setAnalysisVolumeDisplayOffset({
        analysisId: analysis.id,
        offset,
      }),
    );
  }, [
    analysis.volumeAnalysisResult,
    analysis.volumeDisplayOffset,
    analysis.id,
    dispatch,
  ]);

  const palette = (
    <Dropdown
      variant="underlined"
      options={colormapOptions}
      value={colormapName}
      dark
      onChange={(e) => changeColormap(e.target.value)}
    />
  );
  if (analysis.kind === "cad") {
    return (
      <CadDeviationControls
        flushOnBlur
        analysis={analysis}
        width={width}
        palette={palette}
        onChange={(settings) =>
          dispatch(
            setCadDeviationDisplay({ analysisId: analysis.id, ...settings }),
          )
        }
      />
    );
  }

  return (
    <Stack spacing={1} sx={{ width }}>
      {palette}

      {volumeAnalysisMode && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Contour lines:
            </FaroText>
            <TextField
              variant="underlined"
              fullWidth
              dark
              type="number"
              text={(analysis.volumeDisplayNbIsoLines ?? 0).toString()}
              onTextChanged={changeIsoLines}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Raise mesh
            </FaroText>
            <FaroSwitch
              dark
              checked={analysis.volumeDisplayOffset !== undefined}
              onToggle={toggleLiftupPreview}
            />
          </Stack>
        </>
      )}

      {!volumeAnalysisMode && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Tolerance:
            </FaroText>
            <DistanceInputField
              variant="underlined"
              fullWidth
              dark
              distanceInMeter={analysis.tolerance}
              distanceUnit={activeUnit}
              errorMessage="Invalid number"
              onDistanceChanged={changeTolerance}
              debounceMs={textInputDebounceTime}
              inputProps={{ style: { textAlign: "right" } }}
            />
            <FaroText variant="labelL" className={NO_TRANSLATE_CLASS} dark>
              {getDistanceUnitSymbolText(activeUnit)}
            </FaroText>
          </Stack>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <FaroText variant="labelL" dark>
              Range:
            </FaroText>
            <DistanceInputField
              variant="underlined"
              fullWidth
              dark
              distanceInMeter={analysis.searchDistance}
              distanceUnit={activeUnit}
              errorMessage="Invalid number"
              onDistanceChanged={changeRange}
              debounceMs={textInputDebounceTime}
              inputProps={{ style: { textAlign: "right" } }}
            />
            <FaroText variant="labelL" className={NO_TRANSLATE_CLASS} dark>
              {getDistanceUnitSymbolText(activeUnit)}
            </FaroText>
          </Stack>
        </>
      )}
    </Stack>
  );
}

type CadDisplayChange = {
  tolerance: number;
  searchDistance: number;
  deviationColouring?: CadDeviationColouring;
};
type CadDisplayDraft = {
  tolerance: number | undefined;
  searchDistance: number | undefined;
  outerBandLimit: number | undefined;
  mode: "bands" | "continuous";
  unmatched: "source" | "grey";
  unmatchedOpacity?: number;
  explicit: boolean;
};

/** @returns CAD-only display controls, retaining invalid drafts until the whole combination is valid. */
export function CadDeviationControls({
  analysis,
  onChange,
  palette,
  width,
  onValidityChange,
  flushOnBlur,
}: {
  analysis: CadDeviationSettings & { id?: string };
  onChange(settings: CadDisplayChange): void;
  onValidityChange?(valid: boolean): void;
  flushOnBlur?: boolean;
  palette?: ReactNode;
  width?: CSSProperties["width"];
}): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const unit = isMetric(distanceUnit)
    ? DistanceUnitTypes.millimeters
    : DistanceUnitTypes.decimalInches;
  const initial: CadDisplayDraft = {
    tolerance: analysis.tolerance,
    searchDistance: analysis.searchDistance,
    outerBandLimit: analysis.deviationColouring?.outerBandLimit,
    mode: analysis.deviationColouring?.mode ?? "continuous",
    unmatched: analysis.deviationColouring?.unmatched ?? "source",
    unmatchedOpacity: analysis.deviationColouring?.unmatchedOpacity,
    explicit: !!analysis.deviationColouring,
  };
  const [draft, setDraft] = useState(initial);
  const current = useRef(draft);
  const commit = useRef(onChange);
  commit.current = onChange;
  useEffect(() => {
    const next: CadDisplayDraft = {
      tolerance: analysis.tolerance,
      searchDistance: analysis.searchDistance,
      outerBandLimit: analysis.deviationColouring?.outerBandLimit,
      mode: analysis.deviationColouring?.mode ?? "continuous",
      unmatched: analysis.deviationColouring?.unmatched ?? "source",
      unmatchedOpacity: analysis.deviationColouring?.unmatchedOpacity,
      explicit: !!analysis.deviationColouring,
    };
    current.current = next;
    setDraft(next);
  }, [
    analysis.id,
    analysis.tolerance,
    analysis.searchDistance,
    analysis.deviationColouring,
  ]);
  function edit(changes: Partial<CadDisplayDraft>): void {
    const next = { ...current.current, ...changes };
    if (
      changes.mode === "bands" &&
      next.tolerance !== undefined &&
      next.searchDistance !== undefined &&
      (next.outerBandLimit === undefined ||
        next.outerBandLimit <= next.tolerance ||
        next.outerBandLimit >= next.searchDistance)
    ) {
      next.outerBandLimit = initialCadOuterLimit(
        next.tolerance,
        next.searchDistance,
      );
      if (next.outerBandLimit === undefined) return;
    }
    current.current = next;
    setDraft(next);
    const style = {
      mode: next.mode,
      unmatched: next.unmatched,
      outerBandLimit: next.outerBandLimit,
      ...(next.unmatchedOpacity === undefined
        ? {}
        : { unmatchedOpacity: next.unmatchedOpacity }),
    };
    if (
      next.tolerance !== undefined &&
      next.searchDistance !== undefined &&
      isCadDeviationColouring(style, next.tolerance, next.searchDistance)
    ) {
      commit.current({
        tolerance: next.tolerance,
        searchDistance: next.searchDistance,
        deviationColouring: next.explicit ? style : undefined,
      });
    }
  }
  const valid =
    draft.tolerance !== undefined &&
    draft.searchDistance !== undefined &&
    isCadDeviationColouring(draft, draft.tolerance, draft.searchDistance);
  const bandsAvailable =
    draft.tolerance !== undefined &&
    draft.searchDistance !== undefined &&
    initialCadOuterLimit(draft.tolerance, draft.searchDistance) !== undefined;
  function selectUnmatched(choice: "grey" | "source" | "hide"): void {
    edit({
      unmatched: choice === "hide" ? current.current.unmatched : choice,
      unmatchedOpacity: choice === "hide" ? 0 : 1,
      explicit: true,
    });
  }
  useEffect(() => {
    onValidityChange?.(valid);
  }, [onValidityChange, valid]);
  return (
    <Stack
      role="group"
      aria-label="CAD deviation settings"
      spacing={1}
      sx={{ width, minWidth: 0 }}
    >
      <ToggleButtonGroup
        sx={cadToggleStyle}
        exclusive
        size="small"
        fullWidth
        value={draft.mode}
        aria-label="Deviation colour mode"
        onChange={(_, mode: unknown) => {
          if (mode === "bands" || mode === "continuous") {
            edit({ mode, explicit: true });
          }
        }}
      >
        <ToggleButton
          value="bands"
          disabled={draft.mode !== "bands" && !bandsAvailable}
        >
          Bands
        </ToggleButton>
        <ToggleButton value="continuous">Continuous</ToggleButton>
      </ToggleButtonGroup>
      {draft.mode === "continuous" && palette}
      <DistanceInputField
        dark
        fullWidth
        label="Tolerance"
        inputProps={{ "aria-label": "Tolerance" }}
        flushOnBlur={flushOnBlur}
        distanceUnit={unit}
        distanceInMeter={draft.tolerance}
        onDistanceChanged={(tolerance) => edit({ tolerance })}
      />
      {draft.mode === "bands" && (
        <DistanceInputField
          dark
          fullWidth
          label="Outer band limit"
          inputProps={{ "aria-label": "Outer band limit" }}
          flushOnBlur={flushOnBlur}
          distanceUnit={unit}
          distanceInMeter={draft.outerBandLimit}
          onDistanceChanged={(outerBandLimit) =>
            edit({ outerBandLimit, explicit: true })
          }
        />
      )}
      <DistanceInputField
        dark
        fullWidth
        label="Search distance"
        inputProps={{ "aria-label": "Search distance" }}
        flushOnBlur={flushOnBlur}
        distanceUnit={unit}
        distanceInMeter={draft.searchDistance}
        onDistanceChanged={(searchDistance) => edit({ searchDistance })}
      />
      {!valid && (
        <Alert severity="warning">
          {draft.mode === "bands"
            ? "Use 0 <= tolerance < outer band limit < search distance."
            : "Use 0 <= tolerance <= search distance."}
        </Alert>
      )}
      <FaroText variant="labelS" dark>
        Unmatched points
      </FaroText>
      <ToggleButtonGroup
        sx={cadToggleStyle}
        exclusive
        size="small"
        fullWidth
        value={draft.unmatchedOpacity === 0 ? "hide" : draft.unmatched}
        aria-label="Unmatched point display"
      >
        <Tooltip title="Show unmatched points in grey">
          <ToggleButton
            value="grey"
            aria-label="Grey"
            onClick={() => selectUnmatched("grey")}
          >
            <CircleIcon fontSize="small" sx={{ color: CAD_UNMATCHED_COLOUR }} />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Show unmatched points in source colour">
          <ToggleButton
            value="source"
            aria-label="Source colour"
            onClick={() => selectUnmatched("source")}
          >
            <PaletteIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Hide unmatched points">
          <ToggleButton
            value="hide"
            aria-label="Hide unmatched points"
            onClick={() => selectUnmatched("hide")}
          >
            <VisibilityOffIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Stack>
  );
}
